// TODO: Replace MOCK_CUSTOMERS with real API calls:
// GET /manager/customers/{tenantId} for list
// GET /manager/customers/{tenantId}/{customerId} for detail
// These endpoints will be built in Phase 3 backend.

import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronRight, LayoutGrid, List, MessageCircle, Pencil, Phone, Search } from 'lucide-react'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import { useLangPrefix } from '../hooks/useLangPrefix'
import useManagerStore from '../store/managerStore'
import { patchContact, getContact } from '../api/contacts'
import ContactActionsBar from '../components/contacts/ContactActionsBar'
import ContactBookingHistory from '../components/contacts/ContactBookingHistory'
import ContactChannelBadge from '../components/contacts/ContactChannelBadge'
import ContactTagsPanel from '../components/contacts/ContactTagsPanel'
import ImportContactsModal from '../components/contacts/ImportContactsModal'
import ExportContactsButton from '../components/contacts/ExportContactsButton'
import { canAccess, RBAC_MODULES } from '../lib/rbac'
import useInfiniteContacts from '../hooks/useInfiniteContacts'
import { getUniqueContactChannels } from '../utils/contactChannels'
import ContactBookingSummary from '../components/contacts/ContactBookingSummary'
import ContactEditForm from '../components/contacts/ContactEditForm'
import ContactVerticalSection from '../components/contacts/ContactVerticalSection'
import { buildContactPatch } from '../utils/contactEditPatch'
import { formatMoney } from '../lib/money'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'
import {
  formatContactDob,
  formatContactGender,
  toDobDisplayValue,
} from '../utils/contactFields'

const MOCK_CUSTOMERS = [
  {
    id: 'c1', name: 'Truong Nguyen Khoa', phone: '0909666285',
    email: 'nguyenkhoa42856123@gmail.com', visits: 56, lastStatus: 'deposit_pending',
    channels: [], avatar_url: null,
    bookings: [
      { id: 'OB34WCB8', date: '20:00 01/02/2026', source: 'Zalo PA', status: 'confirmed',       requests: '',                    notes: '' },
      { id: 'IJ9LY5TA', date: '20:00 01/02/2026', source: 'Zalo PA', status: 'deposit_pending', requests: '',                    notes: '' },
      { id: '166M7VST', date: '20:00 30/01/2026', source: 'Zalo PA', status: 'confirmed',       requests: '',                    notes: '' },
    ],
  },
  {
    id: 'c2', name: 'Hoàng Hải', phone: '0913050095', email: '', visits: 49, lastStatus: 'confirmed',
    channels: [], avatar_url: null,
    bookings: [
      { id: 'TQ7P10RW', date: '19:00 24/01/2026', source: 'Zalo PA', status: 'confirmed', requests: '', notes: '' },
    ],
  },
  {
    id: 'c3', name: 'Nhân', phone: '0565766636', email: '', visits: 39, lastStatus: 'pending',
    channels: [], avatar_url: null,
    bookings: [
      { id: 'J3GX7DIW', date: '20:00 08/01/2026', source: 'Zalo PA', status: 'pending',
        requests: 'Chuẩn bị trước: - Chuống đúc lò: 3 phần',
        notes: 'Khách đổi sang đặt món trước cho bàn 18h' },
    ],
  },
  {
    id: 'c4', name: 'Trần Phúc Vinh', phone: '0365331185', email: 'pv@gmail.com', visits: 17, lastStatus: 'pending',
    channels: [], avatar_url: null,
    bookings: [
      { id: '8MCBL4TY', date: '18:00 19/01/2026', source: 'Zalo PA', status: 'confirmed', requests: '',                          notes: '' },
      { id: 'CNQWNG7V', date: '19:00 09/01/2026', source: 'Zalo PA', status: 'cancelled', requests: '5 phần Gỏi cuốn hoa bướm',  notes: 'Chuẩn bị sẵn trước khi khách đến' },
    ],
  },
  { id: 'c5', name: 'Nguyễn Hoàng',  phone: '0901234567', email: '',              visits: 28, lastStatus: 'confirmed', channels: [], avatar_url: null, bookings: [] },
  { id: 'c6', name: 'Lê Thị Mai',    phone: '0912345678', email: 'mai@gmail.com', visits: 12, lastStatus: 'pending',   channels: [], avatar_url: null, bookings: [] },
]

const STATUS = {
  confirmed:       { key: 'confirmed',       bg: 'bg-[#e8f5e9]', text: 'text-[#2e7d32]' },
  pending:         { key: 'pending',         bg: 'bg-[#fff8e1]', text: 'text-[#f57f17]' },
  deposit_pending: { key: 'deposit_pending', bg: 'bg-[#f3e5f5]', text: 'text-[#6a1b9a]' },
  cancelled:       { key: 'cancelled',       bg: 'bg-[#ffebee]', text: 'text-[#c62828]' },
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('vi-VN')
}

function StatusPill({ status, t }) {
  const s = STATUS[status] || STATUS.pending
  return <span className={`pill ${s.bg} ${s.text}`}>{t(`customers.bookingStatus.${s.key}`)}</span>
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2 mb-2 text-sm">
      <span className="text-gray-400 w-28 flex-shrink-0">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  )
}

function ContactGridCard({ contact, onClick }) {
  const visibleChannels = getUniqueContactChannels(contact.channels)

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl p-3 flex flex-col items-center gap-2 cursor-pointer active:scale-[0.99] transition-transform w-full"
      style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      <div className="w-12 h-12 rounded-full bg-[#e3eaf8] flex items-center justify-center text-[#1535c8] text-base font-bold flex-shrink-0 overflow-hidden">
        {contact.avatar_url
          ? <img src={contact.avatar_url} alt={contact.name} className="w-full h-full object-cover" />
          : contact.name.charAt(0).toUpperCase()
        }
      </div>
      <div className="w-full text-center">
        <div className="text-sm font-bold text-gray-900 truncate">{contact.name}</div>
        {visibleChannels.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-center mt-1">
            {visibleChannels.map((ch, i) => (
              <ContactChannelBadge key={i} platform={ch.platform} />
            ))}
          </div>
        )}
        <ContactBookingSummary summary={contact.booking_summary} centered />
      </div>
    </button>
  )
}

function CustomerStatTile({ label, value }) {
  return (
    <div className="flex-1 bg-gray-50 rounded-md px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm font-semibold text-gray-800 mt-0.5">{value}</div>
    </div>
  )
}

function normalizeCustomer(contact) {
  return {
    ...contact,
    name: contact.name || 'Unknown',
    phone: contact.phone || '',
    email: contact.email || '',
    visits: contact.transaction_count || 0,
    lastStatus: 'customer',
    bookings: contact.bookings || [],
    user_id: contact.channels?.[0]?.platform_id || null,
  }
}

const CUSTOMER_EDIT_FIELDS = ['name', 'phone', 'email', 'address', 'street', 'ward', 'district', 'province', 'latitude', 'longitude', 'gender', 'dob', 'stage', 'notes']

export default function CustomersPage() {
  const { t, i18n } = useTranslation()
  // Totals wait for a resolved tenant currency: while loading, on error or
  // without a tenant the hook only has the VND placeholder.
  const { currency, isResolved: currencyResolved } = useTenantCurrencyState()
  const navigate = useNavigate()
  const { prefix } = useLangPrefix()
  const { tenantId, role } = useManagerStore()
  const canViewBookingHistory = canAccess(role, RBAC_MODULES.bookings)
  const [searchQuery, setSearchQuery]           = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [showDetail, setShowDetail]             = useState(false)
  const [viewMode, setViewMode]                 = useState(() => localStorage.getItem('growchat.customers.viewMode') || 'list')
  const [importOpen, setImportOpen]             = useState(false)
  const [editMode, setEditMode]                 = useState(false)
  const [editDraft, setEditDraft]               = useState(null)
  const [saving, setSaving]                     = useState(false)
  const [contactDetailLoading, setContactDetailLoading] = useState(false)
  const {
    contacts: customers,
    setContacts: setCustomers,
    loading: contactsLoading,
    hasMore,
    total: customersTotal,
    error: contactsError,
    refresh: loadContacts,
    loadMoreRef,
  } = useInfiniteContacts({ tenantId, stage: 'customer', search: searchQuery, mapContact: normalizeCustomer })

  useEffect(() => {
    localStorage.setItem('growchat.customers.viewMode', viewMode)
  }, [viewMode])

  async function loadCustomerDetail(contactId) {
    if (!tenantId || !contactId) return
    setContactDetailLoading(true)
    try {
      const detail = await getContact(tenantId, contactId)
      const mergeDetail = current => {
        if (!current || current.id !== contactId) return current
        return {
          ...current,
          ...detail,
          name: detail.name || current.name || 'Unknown',
          phone: detail.phone ?? current.phone ?? '',
          email: detail.email ?? current.email ?? '',
          bookings: detail.bookings ?? current.bookings ?? [],
          visits: detail.transaction_count ?? current.visits ?? 0,
          user_id: detail.channels?.[0]?.platform_id || current.user_id || null,
        }
      }
      setSelectedCustomer(mergeDetail)
      setCustomers(prev => prev.map(customer => (
        customer.id === contactId ? mergeDetail(customer) : customer
      )))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'get_contact_detail' },
        extra: { tenantId, contactId },
      })
    } finally {
      setContactDetailLoading(false)
    }
  }

  // `?contact=<id>` opens that contact's detail panel directly — where a
  // lead notification points (see NotificationContactRedirect in App.jsx).
  // The contact is fetched by id rather than looked up in the list: a fresh
  // lead is not a "customer" yet and may not be on the loaded page. The
  // parameter is dropped once handled so closing the panel does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams()
  const contactParam = searchParams.get('contact')
  useEffect(() => {
    if (!tenantId || !contactParam) return undefined
    let cancelled = false
    getContact(tenantId, contactParam)
      .then((detail) => {
        if (cancelled) return
        setSelectedCustomer(normalizeCustomer(detail))
        setShowDetail(true)
      })
      .catch((err) => {
        if (cancelled) return
        if (err?.response?.status === 404) {
          toast.error(t('customers.contactNotFound'))
          return
        }
        toast.error(t('common.requestFailed'))
        Sentry.captureException(err, {
          tags: { action: 'open_contact_from_link' },
          extra: { tenantId, contactId: contactParam },
        })
      })
      .finally(() => {
        if (cancelled) return
        setSearchParams((params) => {
          const next = new URLSearchParams(params)
          next.delete('contact')
          return next
        }, { replace: true })
      })
    return () => {
      cancelled = true
    }
  }, [tenantId, contactParam, setSearchParams, t])

  function openDetail(customer) {
    setSelectedCustomer(customer)
    setShowDetail(true)
    loadCustomerDetail(customer.id)
  }

  function closeDetail() {
    setShowDetail(false)
    setSelectedCustomer(null)
    setEditMode(false)
    setEditDraft(null)
    setSaving(false)
    setContactDetailLoading(false)
  }

  function startEdit() {
    if (!selectedCustomer) return
    setEditDraft({
      name:    selectedCustomer.name    ?? '',
      phone:   selectedCustomer.phone   ?? '',
      email:   selectedCustomer.email   ?? '',
      address: selectedCustomer.address ?? '',
      street:   selectedCustomer.street   ?? '',
      ward:     selectedCustomer.ward     ?? '',
      district: selectedCustomer.district ?? '',
      province: selectedCustomer.province ?? '',
      latitude:  selectedCustomer.latitude  ?? '',
      longitude: selectedCustomer.longitude ?? '',
      stage:   selectedCustomer.stage   ?? 'customer',
      notes:   selectedCustomer.notes   ?? '',
      gender:  selectedCustomer.gender  ?? '',
      dob:     toDobDisplayValue(selectedCustomer.dob),
    })
    setEditMode(true)
  }

  function cancelEdit() {
    setEditMode(false)
    setEditDraft(null)
  }

  function updateSelectedCustomerTags(next) {
    if (!selectedCustomer?.id) return
    setSelectedCustomer(c => (c ? { ...c, tags: next } : c))
    setCustomers(prev => prev.map(c =>
      c.id === selectedCustomer.id ? { ...c, tags: next } : c
    ))
  }

  async function saveEdit() {
    if (!selectedCustomer?.id || !editDraft) return
    const { patch, error } = buildContactPatch({ original: selectedCustomer, draft: editDraft, fields: CUSTOMER_EDIT_FIELDS, t })
    if (error) {
      alert(error)
      return
    }
    if (Object.keys(patch).length === 0) {
      setEditMode(false)
      setEditDraft(null)
      return
    }
    setSaving(true)
    try {
      const updated = await patchContact(tenantId, selectedCustomer.id, patch)
      setSelectedCustomer(current => current?.id === selectedCustomer.id
        ? {
            ...current,
            ...(updated || {}),
            booking_summary: updated?.booking_summary ?? current.booking_summary,
            bookings: updated?.bookings ?? current.bookings ?? [],
            tags: updated?.tags ?? current.tags,
          }
        : updated
      )
      setEditMode(false)
      setEditDraft(null)
      loadContacts()
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'patch_contact' } })
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <ResponsiveShell title={t('customers.title')}>
        <div className="bg-[#f2f4f8] pb-[10px]">

          {/* Section 1 — Search bar + view toggle */}
          <div className="sticky top-0 bg-[#f2f4f8] pt-2 pb-3 z-20">
            <div className="bg-white rounded-xl px-3 py-2.5 flex items-center gap-2" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <Search size={14} className="text-zinc-400" />
              <input
                className="flex-1 text-sm text-gray-800 placeholder-gray-400 outline-none border-none bg-transparent"
                placeholder={t('customers.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between mt-2 gap-2">
              <ContactActionsBar
                tenantId={tenantId}
                defaultStage="customer"
                onContactCreated={loadContacts}
                onImportClick={() => setImportOpen(true)}
              />
              <div className="flex items-center gap-2">
                <ExportContactsButton
                  tenantId={tenantId}
                  stage="customer"
                  search={searchQuery}
                  filenamePrefix="customers"
                />
                <div className="flex border border-slate-200 rounded-md">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-l-md transition-colors ${viewMode === 'list' ? 'bg-[#e3eaf8] text-[#1535c8]' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    <List size={15} />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-r-md transition-colors ${viewMode === 'grid' ? 'bg-[#e3eaf8] text-[#1535c8]' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    <LayoutGrid size={15} />
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('customers.foundCount', { count: customersTotal })}</p>
          </div>

          {/* Section 2 — Customer list or grid */}
          {viewMode === 'list' ? (
            <div>
              {customers.map(customer => (
                <button
                  key={customer.id}
                  className="w-full text-left bg-white rounded-2xl p-4 mb-2.5 flex items-start gap-3 cursor-pointer active:scale-[0.99] transition-transform"
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                  onClick={() => openDetail(customer)}
                >
                  <div className="w-11 h-11 rounded-full bg-[#e3eaf8] flex items-center justify-center text-[#1535c8] text-base font-bold flex-shrink-0 overflow-hidden">
                    {customer.avatar_url
                      ? <img src={customer.avatar_url} alt={customer.name} className="w-full h-full object-cover" />
                      : customer.name.charAt(0).toUpperCase()
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900">{customer.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{t('customers.phoneLabel')} {customer.phone}</div>
                    {customer.email ? <div className="text-xs text-gray-400 truncate">{t('customers.emailLabel')} {customer.email}</div> : null}
                    <ContactBookingSummary summary={customer.booking_summary} />
                  </div>
                  <ChevronRight size={16} className="text-gray-300 self-center flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {customers.map(customer => (
                <ContactGridCard
                  key={customer.id}
                  contact={customer}
                  onClick={() => openDetail(customer)}
                />
              ))}
            </div>
          )}
          {contactsError && !contactsLoading && (
            <button type="button" onClick={loadContacts} className="w-full py-4 text-sm text-red-600">
              {t?.('common.requestFailed') || 'Request failed'} — {t?.('common.retry') || 'Retry'}
            </button>
          )}
          {!contactsError && (hasMore || contactsLoading) && (
            <div ref={loadMoreRef} className="py-5 text-center text-sm text-gray-400">
              {contactsLoading ? (t?.('common.loading') || 'Loading…') : null}
            </div>
          )}
        </div>
      </ResponsiveShell>

      {/* Section 3 — Full-screen customer detail */}
      {showDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-[#f2f4f8] z-50 overflow-y-auto flex flex-col">
          <div className="bg-[#1535c8] px-4 pt-4 pb-4 flex items-center gap-3">
            <button onClick={closeDetail}>
              <ArrowLeft size={20} className="text-white" />
            </button>
            <span className="text-white font-bold text-base flex-1 text-center">{t('customers.customerDetails')}</span>
            {editMode ? (
              <div className="flex items-center gap-3">
                <button onClick={cancelEdit} className="text-white text-sm font-medium opacity-75 hover:opacity-100">
                  {t?.('contacts.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="text-white text-sm font-bold disabled:opacity-50"
                >
                  {saving ? (t?.('contacts.saving') || 'Saving…') : (t?.('contacts.save') || 'Save')}
                </button>
              </div>
            ) : (
              <button onClick={startEdit} className="text-white opacity-75 hover:opacity-100">
                <Pencil size={16} />
              </button>
            )}
          </div>

          <div className="flex-1 px-4 pt-4 pb-8">
            <div className="bg-white rounded-2xl p-4 mb-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

              {/* Avatar + name + status pill — always visible */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 bg-[#e3eaf8] rounded-full flex items-center justify-center text-[#1535c8] text-xl font-bold flex-shrink-0">
                  {selectedCustomer.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-base font-bold text-gray-900">{selectedCustomer.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5">{selectedCustomer.phone}</div>
                  <div className="mt-1"><StatusPill status={selectedCustomer.lastStatus} t={t} /></div>
                </div>
              </div>

              {/* Edit mode: form */}
              {editMode && editDraft && (
                <ContactEditForm
                  fields={CUSTOMER_EDIT_FIELDS}
                  draft={editDraft}
                  setDraft={setEditDraft}
                  t={t}
                />
              )}

              {/* Read mode: all sections */}
              {!editMode && (
                <>
                  {/* Section 1: Contact Info */}
                  {(selectedCustomer.phone || selectedCustomer.email || selectedCustomer.address || selectedCustomer.street || selectedCustomer.ward || selectedCustomer.district || selectedCustomer.province || selectedCustomer.latitude != null || selectedCustomer.longitude != null || selectedCustomer.stage || selectedCustomer.gender || selectedCustomer.dob) && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t?.('contacts.contactInfo') || 'Contact Info'}
                      </div>
                      {selectedCustomer.phone   && <InfoRow label={t?.('contacts.phone')   || 'Phone'}   value={selectedCustomer.phone} />}
                      {selectedCustomer.email   && <InfoRow label={t?.('contacts.email')   || 'Email'}   value={selectedCustomer.email} />}
                      {selectedCustomer.address && <InfoRow label={t?.('contacts.address') || 'Address'} value={selectedCustomer.address} />}
                      {selectedCustomer.street   && <InfoRow label={t?.('contacts.street')   || 'Street'}       value={selectedCustomer.street} />}
                      {selectedCustomer.ward     && <InfoRow label={t?.('contacts.ward')     || 'Ward'}         value={selectedCustomer.ward} />}
                      {selectedCustomer.district && <InfoRow label={t?.('contacts.district') || 'District'}     value={selectedCustomer.district} />}
                      {selectedCustomer.province && <InfoRow label={t?.('contacts.province') || 'Province/City'} value={selectedCustomer.province} />}
                      {(selectedCustomer.latitude != null && selectedCustomer.longitude != null) && (
                        <InfoRow
                          label={t?.('contacts.coordinates') || 'Coordinates'}
                          value={`${selectedCustomer.latitude}, ${selectedCustomer.longitude}`}
                        />
                      )}
                      {selectedCustomer.stage   && <InfoRow label={t?.('contacts.stage')   || 'Stage'}   value={selectedCustomer.stage} />}
                      <InfoRow label={t?.('contacts.gender') || 'Gender'} value={formatContactGender(selectedCustomer.gender, t)} />
                      <InfoRow label={t?.('contacts.dob') || 'Date of birth'} value={formatContactDob(selectedCustomer.dob, t)} />
                    </div>
                  )}

                  {/* Section 2: Channels */}
                  {getUniqueContactChannels(selectedCustomer.channels).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t?.('contacts.channels') || 'Channels'}
                      </div>
                      <div className="space-y-2">
                        {getUniqueContactChannels(selectedCustomer.channels).map((ch, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <ContactChannelBadge platform={ch.platform} />
                            <span
                              className="font-mono text-xs text-gray-500 truncate max-w-[140px]"
                              title={ch.platform_id}
                            >
                              {ch.platform_id || '—'}
                            </span>
                            <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                              {formatDate(ch.followed_at)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section 3: Activity */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {t?.('contacts.activity') || 'Activity'}
                    </div>
                    <div className="flex gap-2">
                      <CustomerStatTile
                        label={t?.('contacts.transactionCount') || 'Transactions'}
                        value={selectedCustomer.transaction_count ?? selectedCustomer.visits ?? 0}
                      />
                      <CustomerStatTile
                        label={t?.('contacts.totalSpent') || 'Total Spent'}
                        value={currencyResolved
                          ? formatMoney(selectedCustomer.total_spent ?? 0, currency, i18n.language)
                          : <span role="status" aria-label={t('common.loading')}>—</span>}
                      />
                      <CustomerStatTile
                        label={t?.('contacts.lastTransaction') || 'Last Transaction'}
                        value={formatDate(selectedCustomer.last_transaction_at)}
                      />
                    </div>
                    {(selectedCustomer.follow_sources || []).length > 0 && (
                      <div className="mt-2">
                        <InfoRow
                          label={t?.('contacts.followSources') || 'Follow Sources'}
                          value={selectedCustomer.follow_sources.join(', ')}
                        />
                      </div>
                    )}
                  </div>

                  <ContactTagsPanel
                    tenantId={tenantId}
                    contactId={selectedCustomer.id}
                    tags={selectedCustomer.tags}
                    variant="detail"
                    onTagsChange={updateSelectedCustomerTags}
                  />

                  <ContactVerticalSection
                    tenantId={tenantId}
                    contact={selectedCustomer}
                    t={t}
                    onUpdated={(updated) =>
                      setSelectedCustomer(current => current?.id === selectedCustomer.id
                        ? {
                            ...current,
                            ...(updated || {}),
                            booking_summary: updated?.booking_summary ?? current.booking_summary,
                            bookings: updated?.bookings ?? current.bookings ?? [],
                            tags: current.tags,
                          }
                        : current
                      )
                    }
                  />

                  {selectedCustomer.notes ? (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t?.('contacts.notes') || 'Notes'}
                      </div>
                      <InfoRow label={t?.('contacts.notes') || 'Notes'} value={selectedCustomer.notes} />
                    </div>
                  ) : null}

                  {/* Section 5: Conversations */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {t?.('contacts.conversations') || 'Conversations'}
                    </div>
                  </div>

                  {/* Call + Message buttons */}
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <button
                      className={`${selectedCustomer.phone ? 'bg-[#1535c8] text-white' : 'border border-gray-300 cursor-not-allowed text-gray-300'} rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold`}
                      onClick={() => window.open('tel:' + selectedCustomer.phone)}
                      disabled={!selectedCustomer.phone}
                    >
                      <Phone size={16} />
                      {t('customers.call')}
                    </button>
                    <button
                      className="border border-[#1535c8] text-[#1535c8] rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold"
                      onClick={() => {
                        closeDetail()
                        if (!tenantId) return
                        const userId = selectedCustomer.user_id || null
                        if (userId) {
                          navigate(`${prefix}/inbox?user=${userId}`)
                        } else {
                          navigate(`${prefix}/inbox?search=${encodeURIComponent(selectedCustomer.phone || selectedCustomer.name || '')}`)
                        }
                      }}
                    >
                      <MessageCircle size={16} />
                      {t('customers.message')}
                    </button>
                  </div>
                </>
              )}
            </div>

            <ContactBookingHistory
              tenantId={tenantId}
              contactId={selectedCustomer.id}
              bookingSummary={selectedCustomer.booking_summary}
              summaryLoading={contactDetailLoading}
              canViewHistory={canViewBookingHistory}
            />
          </div>
        </div>
      )}

      <ImportContactsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        tenantId={tenantId}
        defaultStage="customer"
        onImported={loadContacts}
      />
    </>
  )
}
